import { ChangeEvent, FC, forwardRef, ReactNode, RefObject } from 'react';
import classNames from 'classnames';

import InfoIcon from 'shared/assets/images/icons/info-small.svg';
import { Tooltip } from '../Tooltip/Tooltip';
import { Typography } from '../Typography/Typography';
import styles from './Input.module.css';

type InputType = 'text' | 'password' | 'email' | 'number' | 'phone';

interface InputProps {
	id?: string;
	label?: string;
	placeholder?: string;
	type?: InputType;
	error?: string;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	value?: string;
	defaultValue?: string;
	name?: string;
	className?: string;
	step?: number;
	disabled?: boolean;
	postfix?: ReactNode;
	tooltipMessage?: string;
	hideErrorMessage?: boolean;
	onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const Input: FC<InputProps> = forwardRef(
	(
		{
			id,
			label = ``,
			placeholder = ``,
			name,
			type = `text`,
			error,
			onChange,
			onBlur,
			value,
			defaultValue,
			className,
			disabled,
			step,
			tooltipMessage,
			hideErrorMessage,
		},
		ref,
	) => {
		const { Text } = Typography;

		return (
			<label htmlFor={id} className={classNames(styles[`input__label-wrapper`], className)}>
				{label && (
					<div className={styles.input__label}>
						<Text variant='note'>{label}</Text>
						{tooltipMessage && (
							<Tooltip
								message={tooltipMessage}
								className={styles.input__tooltip}
								messageWidth='15rem'
								size='small'
								position={[`middle`, `left-side`]}
							>
								<InfoIcon />
							</Tooltip>
						)}
					</div>
				)}
				<div className={styles[`input__field-wrapper`]}>
					<input
						className={classNames(
							styles.input__field,
							error && styles[`input__field--error`],
							disabled && styles[`input__field--disabled`],
						)}
						autoComplete='new-password' // to prevent autocomplete ref:https://stackoverflow.com/a/51686518
						type={type}
						placeholder={placeholder}
						id={id}
						ref={ref as RefObject<HTMLInputElement>}
						value={value}
						onChange={onChange}
						onBlur={onBlur}
						defaultValue={defaultValue}
						name={name}
						disabled={disabled}
						step={step}
					/>
				</div>
				{error && !hideErrorMessage && (
					<Text variant='note' className={styles[`input__error-text`]}>
						{error}
					</Text>
				)}
			</label>
		);
	},
);
